import { useEffect, useMemo } from 'react'
import { useAppStore } from '../../store/app'
import { NavBar, NavBarButton } from '../../components/NavBar'
import { SwipeableRow } from '../../components/SwipeableRow'
import {
  archiveScheduledTrigger,
  listScheduledTriggers,
  updateScheduledTrigger,
  type ScheduledTriggerInput,
} from '../../api/ws'
import type { ScheduledTrigger } from '../../store/types'
import { cronToText } from './schedule'
import { parseServerTime, runStatusLabel } from './runFormat'
import './routines.css'

// Build the full update payload from an existing row — the server's update
// handler requires every field, so a one-field change (the enabled toggle)
// resends the rest unchanged.
function triggerToInput(t: ScheduledTrigger, overrides: Partial<ScheduledTriggerInput> = {}): ScheduledTriggerInput {
  return {
    name: t.name,
    enabled: t.enabled,
    cron_expr: t.cron_expr,
    timezone: t.timezone,
    target_mode: t.target_mode,
    target_ai_agent_instance_id: t.target_ai_agent_instance_id,
    recipe_overrides_json: t.recipe_overrides_json,
    prompt: t.prompt,
    catch_up_window_minutes: t.catch_up_window_minutes,
    overlap_policy: t.overlap_policy,
    // End conditions must ride along on every update, or flicking the enable
    // toggle here would silently clear the Routine's end date — the handler
    // writes whatever it is sent, and an absent field means "no bound".
    //
    // Converted back to RFC3339: the row carries the stored SQL form, the
    // request wants an instant.
    ends_at: serverTimeToISO(t.ends_at),
    max_runs: t.max_runs ?? 0,
    ...overrides,
  }
}

// serverTimeToISO re-encodes a stored DATETIME for a request body. An empty or
// unparseable value stays empty (no bound) — sending garbage back would fail
// validation on an edit the user did not make.
function serverTimeToISO(value: string | null): string {
  const ms = parseServerTime(value ?? '')
  return Number.isNaN(ms) ? '' : new Date(ms).toISOString()
}

// Row-sized version of runStatusLabel: same vocabulary, but the skipped_*
// reasons are trimmed to fit a list row (the detail is on the run itself).
// Delegating rather than repeating the switch — the two had already drifted
// once, and a status this file doesn't know about should read the same here as
// it does everywhere else.
function lastRunLabel(t: ScheduledTrigger): string {
  if (!t.last_run_status) return 'Never run'
  const status = t.last_run_status
  const friendly = status.startsWith('skipped') ? 'Skipped' : runStatusLabel(status)
  const when = t.last_run_at ? ` · ${relativeTime(t.last_run_at)}` : ''
  return `${friendly}${when}`
}

function relativeTime(iso: string): string {
  // Through parseServerTime, not Date.parse: the relay's SQL timestamps carry
  // no zone, so parsing one directly reads UTC as local and shifts every
  // "2h ago" by the viewer's offset.
  const t = parseServerTime(iso)
  if (Number.isNaN(t)) return ''
  const diff = Date.now() - t
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 90) return `${mins}m ago`
  const hrs = Math.round(diff / 3_600_000)
  if (hrs < 48) return `${hrs}h ago`
  return `${Math.round(diff / 86_400_000)}d ago`
}

// A finished Routine reads as its own state, not as "off". The distinction is
// the point: it is not switched off and not deleted — it did what it was asked
// to, and the way back is to edit its end condition.
function endLabel(t: ScheduledTrigger): string | null {
  if (t.completed_at) {
    return t.max_runs ? `Finished · ran ${t.run_count}\u00d7` : "Finished"
  }
  if (t.max_runs) return `${Math.max(0, t.max_runs - t.run_count)} runs left`
  if (t.ends_at) {
    const d = parseServerTime(t.ends_at)
    if (!Number.isNaN(d)) {
      const when = new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })
      return `Until ${when}`
    }
  }
  return null
}

export function RoutinesScreen() {
  const popNav = useAppStore((s) => s.popNav)
  const pushNav = useAppStore((s) => s.pushNav)
  const orgID = useAppStore((s) => s.organizationID)
  const triggers = useAppStore((s) => s.scheduledTriggers)
  const agentInstances = useAppStore((s) => s.agentInstances)

  // The server only pushes scheduled_triggers_list on change, so request it on
  // open (mirrors GrantsSegment's sendListRules on mount).
  useEffect(() => {
    if (orgID) listScheduledTriggers(orgID)
  }, [orgID])

  const agentName = useMemo(() => {
    const byID = new Map(agentInstances.map((a) => [a.id, a.name]))
    return (id: string | null) => (id ? byID.get(id) ?? 'An agent' : 'An agent')
  }, [agentInstances])

  function targetLabel(t: ScheduledTrigger): string {
    return t.target_mode === 'temp' ? 'Fresh agent each run' : agentName(t.target_ai_agent_instance_id)
  }

  function toggleEnabled(t: ScheduledTrigger) {
    void updateScheduledTrigger(t.id, triggerToInput(t, { enabled: !t.enabled }))
  }

  return (
    <div className="routines">
      <NavBar
        title="Routines"
        leading={<NavBarButton icon="‹" label="Back" onClick={popNav} />}
        trailing={
          <NavBarButton icon="+" label="New Routine" onClick={() => pushNav({ kind: 'routine_editor' })} />
        }
      />

      <div className="routines__scroll">
        <p className="routines__intro">
          Routines run an agent on a schedule — like every morning at 5, check the overnight camera
          events and text you anything unusual.
        </p>

        {triggers.length === 0 ? (
          <div className="routines__empty">
            <div className="routines__empty-icon" aria-hidden>
              ⏰
            </div>
            <div className="routines__empty-title">No routines yet</div>
            <div className="routines__empty-body">
              Tap + to schedule an agent to run automatically.
            </div>
          </div>
        ) : (
          <div className="routines__list">
            {triggers.map((t) => (
              <SwipeableRow
                key={t.id}
                trailing={{ label: 'Delete', onAction: () => void archiveScheduledTrigger(t.id) }}
              >
                <div className="routines__row">
                  <button
                    className="routines__row-main"
                    onClick={() => pushNav({ kind: 'routine_editor', triggerID: t.id })}
                  >
                    <div
                      className={`routines__row-name${
                        t.enabled && !t.completed_at ? '' : ' routines__row-name--off'
                      }`}
                    >
                      {t.name || 'Untitled routine'}
                    </div>
                    <div className="routines__row-cadence">{cronToText(t.cron_expr)}</div>
                    <div className="routines__row-meta">
                      {targetLabel(t)} · {lastRunLabel(t)}
                      {endLabel(t) ? ` · ${endLabel(t)}` : ''}
                    </div>
                  </button>
                  <label
                    className="routines__toggle"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={
                      t.completed_at
                        ? 'Routine finished — edit it to extend'
                        : t.enabled
                          ? 'Disable routine'
                          : 'Enable routine'
                    }
                  >
                    {/* Disabled once finished: switching it on would promise a run
                        that cannot happen until the end condition moves. */}
                    <input
                      type="checkbox"
                      checked={t.enabled && !t.completed_at}
                      disabled={!!t.completed_at}
                      onChange={() => toggleEnabled(t)}
                    />
                    <span className="routines__toggle-track" />
                  </label>
                </div>
              </SwipeableRow>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
